"use client";

import React, { useState } from "react";
import { motion } from "framer-motion";
import { Copy, Heart, Check, Sparkles } from "lucide-react";
import { useFavorites } from "@/hooks/useFavorites";
import { useAudioFeedback } from "@/hooks/useAudioFeedback";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { formatName } from "@/lib/utils";
import { toast } from "sonner";

interface NameCardProps {
  name: string | { name: string; meaning?: string };
  category?: string;
  index?: number;
  onInspectLore?: (name: string, meaning?: string) => void;
}

export default function NameCard({
  name,
  category = "Fantasy",
  index = 0,
  onInspectLore,
}: NameCardProps) {
  const { isFavorite, addFavorite, removeFavorite, favorites } = useFavorites();
  const { playPop, playChime } = useAudioFeedback();
  const [copied, setCopied] = useState(false);

  const displayName = typeof name === "string" ? name : name?.name || "";
  const nameAr = typeof name === "object" && (name as any)?.name_ar ? (name as any).name_ar : null;
  const rawMeaning = typeof name === "object" && name?.meaning ? name.meaning : null;
  const favorite = isFavorite(displayName);

  // Extract cultural tags like [Muslim Male] or direct properties (gender, era, religion)
  const objName = typeof name === "object" && name ? (name as any) : null;
  let tagInfo: { religion?: string; gender?: string; era?: string; origin?: string } | null = null;
  let cleanMeaning = rawMeaning;

  if (objName && (objName.gender || objName.religion || objName.era || objName.origin)) {
    tagInfo = {
      gender: objName.gender,
      religion: objName.religion,
      era: objName.era,
      origin: objName.origin,
    };
  }

  if (rawMeaning) {
    const match = rawMeaning.match(/^\[(Muslim|Hindu)\s+(Male|Female)\]\s*(.*)$/i);
    if (match) {
      tagInfo = { ...tagInfo, religion: match[1], gender: match[2] };
      cleanMeaning = match[3];
    }
  }

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(displayName);
      setCopied(true);
      playChime();
      toast.success(`Copied "${displayName}" to clipboard! ✨`);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Failed to copy to clipboard");
    }
  };

  const handleToggleFavorite = () => {
    playPop();
    if (favorite) {
      const match = favorites.find((f) => f.name === displayName);
      if (match) removeFavorite(match.id);
      else removeFavorite(displayName);
      toast.info(`Removed "${displayName}" from favorites`);
    } else {
      addFavorite(displayName, category, cleanMeaning || rawMeaning || undefined);
      toast.success(`Saved "${displayName}" to favorites! ❤️`);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22, delay: Math.min(index * 0.025, 0.35) }}
      className="w-full"
    >
      <Card className="group relative overflow-hidden border border-border/80 bg-card/90 backdrop-blur-sm p-4 sm:p-5 hover:shadow-xl hover:border-primary/50 transition-all duration-300">
        <div className="flex items-center justify-between gap-3">
          {/* Main Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-lg sm:text-xl font-bold font-fantasy text-foreground tracking-wide group-hover:text-primary transition-colors truncate">
                {formatName(displayName)}
              </h3>
              {nameAr && (
                <span
                  dir="rtl"
                  className="text-xs sm:text-sm font-semibold text-emerald-600 dark:text-emerald-400 px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 shrink-0 font-serif"
                >
                  {nameAr}
                </span>
              )}
              {tagInfo && (
                <>
                  {tagInfo.religion && (
                    <Badge
                      variant="outline"
                      className={`text-[10px] h-4 px-1.5 font-semibold ${
                        tagInfo.religion.toLowerCase() === "muslim"
                          ? "border-emerald-500/40 text-emerald-600 dark:text-emerald-400 bg-emerald-500/10"
                          : "border-amber-500/40 text-amber-600 dark:text-amber-400 bg-amber-500/10"
                      }`}
                    >
                      {tagInfo.religion.toLowerCase() === "muslim" ? "🌙 Muslim" : "🕉️ Hindu"}
                    </Badge>
                  )}
                  {tagInfo.gender && (
                    <Badge
                      variant="outline"
                      className={`text-[10px] h-4 px-1.5 font-semibold ${
                        tagInfo.gender.toLowerCase() === "male"
                          ? "border-blue-500/40 text-blue-600 dark:text-blue-400 bg-blue-500/10"
                          : tagInfo.gender.toLowerCase() === "female"
                          ? "border-pink-500/40 text-pink-600 dark:text-pink-400 bg-pink-500/10"
                          : "border-purple-500/40 text-purple-600 dark:text-purple-400 bg-purple-500/10"
                      }`}
                    >
                      {tagInfo.gender.toLowerCase() === "male"
                        ? "♂ Male"
                        : tagInfo.gender.toLowerCase() === "female"
                        ? "♀ Female"
                        : "⚧ Unisex"}
                    </Badge>
                  )}
                  {tagInfo.origin && tagInfo.origin !== "English" && (
                    <Badge
                      variant="outline"
                      className="text-[10px] h-4 px-1.5 font-semibold border-border/80 text-muted-foreground bg-muted/40"
                    >
                      {tagInfo.origin}
                    </Badge>
                  )}
                  {tagInfo.era && (
                    <Badge
                      variant="outline"
                      className="text-[10px] h-4 px-1.5 font-semibold border-amber-500/40 text-amber-700 dark:text-amber-300 bg-amber-500/10"
                    >
                      {tagInfo.era}
                    </Badge>
                  )}
                </>
              )}
              {favorite && (
                <Badge variant="fantasy" className="text-[10px] h-4 px-1.5 shadow-xs">
                  Saved
                </Badge>
              )}
            </div>

            {cleanMeaning && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2 leading-relaxed">
                {cleanMeaning}
              </p>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            {/* Lore / Story Hook Button */}
            {onInspectLore && (
              <Button
                variant="outline"
                size="icon"
                onClick={() => onInspectLore(displayName, cleanMeaning || rawMeaning || undefined)}
                title="Inspect Lore, D&D Profile & Story Hooks"
                aria-label="Inspect Lore & Story Hooks"
                className="rounded-xl h-12 w-12 text-muted-foreground hover:text-primary hover:border-primary/50 transition-all"
              >
                <Sparkles className="w-4 h-4 text-primary/80 group-hover:text-primary" />
              </Button>
            )}

            {/* Heart Favorite Button */}
            <Button
              variant="outline"
              size="icon"
              onClick={handleToggleFavorite}
              aria-label={favorite ? "Remove from favorites" : "Add to favorites"}
              className={`rounded-xl h-12 w-12 transition-all ${
                favorite
                  ? "border-rose-500/50 bg-rose-50 text-rose-600 dark:bg-rose-950/40 dark:text-rose-400"
                  : "text-muted-foreground hover:text-rose-500 hover:border-rose-300 dark:hover:border-rose-800"
              }`}
            >
              <Heart
                className={`w-4 h-4 transition-transform duration-200 ${
                  favorite ? "fill-current scale-110" : "group-hover:scale-105"
                }`}
              />
            </Button>

            {/* Copy Button */}
            <Button
              variant="fantasy"
              size="icon"
              onClick={handleCopy}
              aria-label="Copy name"
              className="rounded-xl h-12 w-12 shadow-xs transition-all active:scale-95"
            >
              {copied ? <Check className="w-4 h-4 text-primary-foreground" /> : <Copy className="w-4 h-4" />}
            </Button>
          </div>
        </div>
      </Card>
    </motion.div>
  );
}
